import data from "../../shared/sharedData";

const isMaxNumWorksites = (formApi) => {
  const numWorksites = formApi.getState().values?.otherWorksites?.length ?? 0;
  return numWorksites >= 250;
};

export default {
  name: "Appendix A, Request for Additional Worksite(s)",
  pageAlert: {
    widget: "pageAlert",
    alerts: [
      {
        title: "Information Required",
        description:
          "Please specify in Field F.e.7 whether work will be performed at multiple worksites.",
        type: "info",
        isVisible: {
          when: "otherWorksiteLocation",
          is: "untouched",
          operator: "untouched",
        },
      },
      {
        title: "Section Complete",
        description:
          "Because you indicated that work will only be performed at one worksite (per Section F.e), no Appendix A is necessary for this application.",
        type: "success",
        isVisible: {
          when: "otherWorksiteLocation",
          is: [[0]],
          operator: "compareMultiFieldValuesAND_ORInArray",
        },
      },
      {
        isVisible: {
          when: isMaxNumWorksites,
          is: "otherWorksitesLengthCheck",
          operator: "customValidation",
        },
        title: "Maximum Number of Worksites Reached",
        description:
          "Appendix A is limited to 250 such worksites. If the employer seeks prevailing wage determinations for more than 250 such worksites, the employer must submit another Form ETA-9141 with a listing of the additional worksites for which prevailing wage determinations are sought.",
        type: "info",
      },
    ],
  },
  pageInfo: {
    text: `<b>Important Note: </b> Employers are required to complete Appendix A if work will be performed in any BLS Area (Metropolitan or Non-Metropolitan Statistical Area) other than the BLS Area provided as the worksite address in Section F.e. In the case of BLS Areas in which there are different prevailing wages in different counties, employers are required to complete Appendix A if work will be performed in any county other than the county provided as the worksite address in Section F.e. If the employer is unsure whether additional worksites are in different BLS Area(s) or counties with different wages, the employer must list the additional worksite in Appendix A. Complete Items 1 (county) and 2 (state), OR alternatively Item 3 (BLS Area), for each worksite for which the employer is requesting a prevailing wage determination. If the employer intends for the workers sought to perform labor or services at more than three (3) worksites that must be reported on Appendix A, the employer must complete additional Appendices A as are necessary to list such worksites for this application. Employers must not complete any fields in the sections designated “for official government use only.” Note that Appendix A is limited to 250 such worksites. If the employer seeks prevailing wage determinations for more than 250 such worksites, the employer must submit another Form ETA-9141 with a listing of the additional worksites for which prevailing wage determinations are sought. <br/>
    <br> 1. Enter the county (or independent city/township(s)/borough(s)/parish(es) as appropriate) of the area of intended employment, and enter the state of the area of intended employment; OR <br/>
    2. Enter the name of the BLS Area (Metropolitan or Non-Metropolitan Statistical Area) of the area of intended employment.<br/>
    <br> Submission of additional worksite information in any other form or format will not be accepted. Only worksites entered on the Form ETA-9141 and Appendix A will be used in the processing of the employer’s request for a prevailing wage determination.`,
    coloring: "grey",
    isVisible: {
      when: "otherWorksiteLocation",
      is: [[1]],
      operator: "compareMultiFieldValuesAND_ORInArray",
    },
  },
  sections: [
    {
      code: "Appendix A",
      name: "Additional Places of Employment",
      sectionInfo: {
        text: `<b>Note: </b> Identify any additional worksite(s) for which the employer is requesting issuance of an additional prevailing wage. `,
        coloring: "grey",
      },
      sectionHeader: "Additional Worksites",
      machine_name: "otherWorksites",
      isModal: true,
      removeFromFieldArray: true,
      openModalHeader: "Add Worksite",
      modalHeader: "Add Additional Places of Employment Information",
      listType: "table",
      validate: ["tableRequired", "tableIncomplete"],
      editButton: true,
      removeLink: "Remove additional place of employment",
      hide: isMaxNumWorksites,
      modalFields: [
        {
          machine_name: "worksiteCity",
          label: "City",
          widget: "text",
          validate: [
            {
              minLength: 2,
              message: "City must contain at least 2 characters",
            },
          ],
          maxLength: 120,
          tooltip: `PERM or LCA: Only the County and State are required.  The Form ETA-9141 determination will not include the City field because it is not relevant to the wage determination.<br>
          H-2B: Enter the city, county and state. This entry will be used when completing the Form ETA-9142B, Appendix A. The city will not appear on the Form ETA-9141, Appendix A.`,
        },
        {
          machine_name: "worksiteState",
          label: "State",
          widget: "select",
          selection: data.state,
          state: true,
          validate: [
            "required",
            {
              notAnyOf: [
                "AMERICAN SAMOA",
                "FEDERATED STATES OF MICRONESIA",
                "MARSHALL ISLANDS",
                "PALAU",
              ],
              message: "Area not available",
            },
          ],
        },
        {
          hideOnTable: true,
          isGroup: {
            machine_name: "worksiteCountyOrBlsArea",
            label: `Please select County or BLS Area`,
            coloring: "none",
            type: "radio",
            // parent_machine_name: "add_values_field",

            fields: [
              {
                widget: "radio",
                choice: "County",
                value: "County",
                validate: ["required"],
                clearSchema: [
                  "worksiteCountyTownship",
                  "worksiteBlsArea",
                  "worksiteBlsAreaId",
                ],
                parent_machine_name: "add_values_field",
              },
              {
                widget: "radio",
                choice: "BLS Area",
                value: "BLS Area",
                validate: ["required"],
                clearSchema: [
                  "worksiteCountyTownship",
                  "worksiteBlsArea",
                  "worksiteBlsAreaId",
                ],
                parent_machine_name: "add_values_field",
              },
            ],
          },
        },
        {
          machine_name: "worksiteCountyTownship",
          widget: "countyselect",
          label: "County",
          parent_machine_name: "add_values_field",
          disabled: true,
          disableWhenField: "worksiteState",
          required: true,
          validate: [
            "required",
            {
              required_county: "worksiteState",
              message: "add_values_field",
              needsFieldValues: true,
            },
          ],
          updateBls: {
            machine_name: "worksiteBlsArea",
            machine_name_id: "worksiteBlsAreaId",
            type: "select",
          },
          isVisible: {
            when: "add_values_field[0]worksiteCountyOrBlsArea",
            is: "County",
          },
          tooltip: `For work locations in New England states: CT, ME, NH, MA, VT, RI use the city or town where the work will be performed as equivalent to the county.`,
        },
        {
          machine_name: "worksiteBlsArea",
          widget: "msaselect",
          label: "BLS Area",
          disabled: true,
          disableWhenField: "worksiteState",
          required: true,
          validate: [
            "required",
            {
              required_county: "worksiteState",
              message: "add_values_field",
              needsFieldValues: true,
            },
          ],
          parent_machine_name: "add_values_field",
          clearSchema: ["worksiteCountyTownship"],
          updateBls: {
            machine_name_id: "worksiteBlsAreaId",
            type: "select",
          },
          isVisible: {
            when: "add_values_field[0]worksiteCountyOrBlsArea",
            is: "BLS Area",
          },
        },
      ],
      label:
        "Use the table below to enter the details about  each additional place of employment, when applicable.",
      isVisible: {
        when: "otherWorksiteLocation",
        is: [[1]],
        operator: "compareMultiFieldValuesAND_ORInArray",
      },
    },
  ],
};
